import { Root, createRoot } from "react-dom/client";
import { FlowchartComponent } from "./flowchart-component";
import { Graph } from "./graph";
import { ReactFlowProvider, Node, Position } from "reactflow";
import { Visualizer } from "../interpretation/representation";
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import { InterpType } from "../interpretation/interpretable";
import React, { useContext } from "react";
import { NarrowedInterpretable } from "../interpretation/interpretable";
import assert from "assert";
import { IdentifierComponent } from "./components/identifier";
import { CustomDragDropContext, Draggable } from "./dragdrop";

export const interpretablesContext: React.Context<Map<string, NarrowedInterpretable>> = React.createContext(undefined);
export class Flowchart extends Visualizer {
  flowchartArea: HTMLElement;
  flowchart: Root;
  graph: Graph;
  interpretables: Map<string, NarrowedInterpretable>;

  constructor(flowchartArea: HTMLElement) {
    super();
    this.flowchartArea = flowchartArea;
    this.flowchart = createRoot(this.flowchartArea);
    this.graph = new Graph();
    this.renderFlowchart();
  }

  /**
   * Gets the shape of a node as a React component.
   * This is generally used for inline shapes.
   *
   * @param interpretable
   * @returns React component
   */
  static getShapeOf(interpretable: NarrowedInterpretable) {
    switch (interpretable.type) {
      case InterpType.Break:
        return;
      case InterpType.Call:
        return;
      case InterpType.Identifier:
        return IdentifierComponent;
      case InterpType.DataLiteral:
        return () => <>[dataliteral]</>;
      case InterpType.ArrayLiteral:
        return () => <>[array]</>;
      case InterpType.FunctionDef:
      case InterpType.If:
      case InterpType.Return:
      case InterpType.BinaryGeneralOp:
      case InterpType.BinaryNumericalOp:
      case InterpType.UnaryOp:
      case InterpType.While:
    }
  }

  setInterpretables(interpretables: Map<string, NarrowedInterpretable>) {
    this.interpretables = interpretables;
    this.populateGraphFrom(Array.from(interpretables.keys()).at(-1));
  }

  private populateGraphFrom(uuid: string): Node[] {
    const interpretable = this.interpretables.get(uuid);
    if (interpretable == undefined) {
      console.error("??");
      return [];
    }
    // Each type must add their own nodes to the graph in left-right form.
    // a return value [with nodes here] signals you want to connect the edges in top-to-bottom format.
    // Blocks connect the nodes top-to-bottom.
    switch (interpretable.type) {
      case InterpType.Assignment:
      case InterpType.Break:
      case InterpType.Call:
      case InterpType.Return:
        const node = {
          id: uuid,
          position: { x: 0, y: 0 },
          type: interpretable.type,
          data: uuid,
        };
        this.graph.addNode(node);
        return [node];
      case InterpType.Block:
        // blocks connect edges of their children
        const children = interpretable.children
          .flatMap((child) => this.populateGraphFrom(child))
          .filter((item) => item !== undefined); // removes unfinished nodes.
        this.connectEdges(children);
        return children;

      case InterpType.ForIter:
      case InterpType.ForLoop:
      case InterpType.While: {
        const start = {
          id: uuid,
          position: { x: 0, y: 0 },
          type: interpretable.type,
          data: uuid,
        };
        this.graph.addNode(start);
        const body = this.populateGraphFrom(interpretable.children.body);
        const end = {
          id: uuid + "-end",
          position: { x: 0, y: 0 },
          type: interpretable.type + "-end",
          data: undefined,
        };
        this.graph.addEdge(uuid + "-end", uuid, Position.Left, Position.Left);
        this.graph.addNode(end);
        return [start, ...body, end];
      }
      case InterpType.FunctionDef: {
        const start = {
          id: uuid,
          position: { x: 0, y: 0 },
          type: interpretable.type,
          data: uuid,
        };
        this.graph.addNode(start);
        const body = this.populateGraphFrom(interpretable.children.body);
        const end = {
          id: uuid + "-end",
          position: { x: 0, y: 0 },
          type: interpretable.type + "-end",
          data: undefined,
        };
        this.graph.addNode(end);
        return [start, ...body, end];
      }
      case InterpType.If:
        const startIf = {
          id: uuid,
          position: { x: 0, y: 0 },
          type: interpretable.type,
          data: uuid,
        };
        this.graph.addNode(startIf);
        // calculating then in between start and end.
        const then = this.populateGraphFrom(interpretable.children.then);
        then.forEach((node) => (node.parentNode = uuid));
        const endIf: Node = {
          id: uuid + "-end",
          position: { x: 0, y: 0 },
          type: interpretable.type + "-end",
          data: undefined,
          parentNode: uuid,
        };
        this.graph.addNode(endIf);
        if (interpretable.children.else) {
          const elsePopulated = this.populateGraphFrom(interpretable.children.else);
          if (elsePopulated.length > 0) {
            // add proper edges to signal the jump:
            this.graph.addEdge(startIf.id, elsePopulated[0].id, Position.Right, Position.Top);
            this.graph.addEdge(elsePopulated.at(-1).id, endIf.id, Position.Bottom, Position.Right);
          }
        }
        return [startIf, ...then, endIf];
      default:
        console.error("node type is inline");
        return [];
    }
  }

  renderFlowchart(locked?: boolean) {
    console.log(this.interpretables)
    console.log("flowchart render called", this.interpretables);
    this.flowchart.render(
      <ReactFlowProvider>
        <CustomDragDropContext
          autoScroll={true}
          collisionDetection={pointerWithin}
          onDragStart={(e) => {
            console.log(e);
            console.log("drag start");
            this.renderFlowchart(true);
          }}
          onDragEnd={(event) => {
            // over : the Droppable
            // active : the Draggable.
            if (!event.over) {
              console.log("dropped onto nothing, skipping");
              return;
            }
            console.warn(event);
            console.warn(this.interpretables);

            console.error(event.active.data.current.uuidOrFunction);
            // check if
            if (typeof event.active.data.current.uuidOrFunction == "function") {
              const droppableUUID = event.over.data.current.uuid as string;
              const droppable = this.interpretables.get(droppableUUID);
              const droppableParentUUID = droppable.parent;
              const droppableParent = this.interpretables.get(droppableParentUUID);
              let droppableIndex;
              for (const key in droppableParent.children) {
                if (droppableParent.children[key] == droppableUUID) {
                  droppableIndex = key;
                }
              }
              assert(droppableIndex);

              const newChild = (event.active.data.current.uuidOrFunction as () => NarrowedInterpretable)();
              newChild.parent = droppableParentUUID;
              droppableParent[droppableIndex] = newChild;
              return;
            }
            // all the following code does is swap the two children's positions.

            const droppableUUID = event.over.data.current.uuid as string;
            const draggableUUID = event.active.data.current.uuidOrFunction as string;
            const droppable = this.interpretables.get(droppableUUID);
            const draggable = this.interpretables.get(draggableUUID);
            // swap parents and positions here
            const droppableParentUUID = droppable.parent;
            const draggableParentUUID = draggable.parent;

            const droppableParent = this.interpretables.get(droppableParentUUID);
            const draggableParent = this.interpretables.get(draggableParentUUID);

            // finding indexes so we can swap them
            let draggableIndex;
            let droppableIndex;
            for (const key in droppableParent.children) {
              if (droppableParent.children[key] == droppableUUID) {
                droppableIndex = key;
              }
            }
            for (const key in draggableParent.children) {
              if (draggableParent.children[key] == draggableUUID) {
                draggableIndex = key;
              }
            }

            console.log(draggableIndex, droppableIndex);

            assert(draggableIndex, "draggableIndex not found in parent");
            assert(droppableIndex, "droppableIndex not found in parent");

            draggableParent.children[draggableIndex] = droppableUUID;
            droppableParent.children[droppableIndex] = draggableUUID;
            draggable.parent = droppableParentUUID;
            droppable.parent = draggableParentUUID;
            this.graph.changeFlag = true;
            this.pushASTChange(this.interpretables);
          }}
        >
          
          <interpretablesContext.Provider value={this.interpretables}>
            <FlowchartComponent graph={this.graph} locked={locked} />
          </interpretablesContext.Provider>
          {/*
          <div className="palette">
            <Draggable
              of={() => {
                return { type: InterpType.Identifier, data: { name: "--id--" }, parent: undefined };
              }}
            >
              <span>identifier</span>
            </Draggable>
          </div>

            */}
        </CustomDragDropContext>
      </ReactFlowProvider>
    );
  }

  connectEdges(nodes: Node<NarrowedInterpretable>[]) {
    for (let i = 0; i < nodes.length; i++) {
      const previous = nodes[i - 1] as Node<NarrowedInterpretable> | undefined;
      const node = nodes[i];
      if (previous) {
        this.graph.addEdge(previous.id, node.id, Position.Bottom, Position.Top);
      }
    }
  }

  clear() {
    this.graph = new Graph();
  }
}

export function useInterpretable<Interpretable extends NarrowedInterpretable>(uuid: string): [Interpretable, (dataToSet: Interpretable['data']) => void] {
  const interpretables = useContext(interpretablesContext);
  const interpretable = interpretables.get(uuid) as Interpretable;
  if (!interpretable) {
    return [undefined, undefined];
  }
  return [interpretable, (dataToSet: Interpretable['data']) => {
    const cloned:Interpretable = JSON.parse(JSON.stringify(interpretable));
    cloned.data = dataToSet;
    interpretables.set(uuid, cloned)
  }];
}
